/**
 * Recipient model of the composer (DESIGN.md T11 RecipientInput): chips with a status, list
 * parsing for typed/pasted text ("Name <a@b>; c@d, …") and deterministic avatar tones.
 * Pure functions, no React.
 */
import { z } from 'zod';

export type RecipientSource = 'recent' | 'contact' | 'gal' | 'external';
export type RecipientKind = 'person' | 'group';
/**
 * resolved = usable address; pending = typed name, not checked yet; ambiguous = several matches
 * (candidates); unresolved = no match in the address book; invalid = malformed address.
 */
export type RecipientStatus = 'resolved' | 'pending' | 'ambiguous' | 'unresolved' | 'invalid';

/** An address book entry (autocomplete suggestion, check-names match, address book row). */
export interface PersonEntry {
  /** Addressless personal groups are expanded before selection; never released as a synthetic address. */
  members?: readonly PersonEntry[];
  /** Server key of the address (photos, "remove from suggestions", group members). */
  key: string;
  email: string;
  name: string | null;
  source: RecipientSource;
  kind: RecipientKind;
}

export interface Recipient {
  /** Client-side id (React key, drag and drop). */
  id: string;
  /** Empty for unresolved names. */
  email: string;
  name?: string | undefined;
  key?: string | undefined;
  source?: RecipientSource | undefined;
  kind: RecipientKind;
  status: RecipientStatus;
  /** The text the user typed (shown again when editing an unresolved chip). */
  input?: string | undefined;
  /** Choices of an ambiguous chip. */
  candidates?: readonly PersonEntry[] | undefined;
}

/** Initial recipients: an address or an address with a display name. */
export type RecipientInit = string | { email: string; name?: string | null | undefined };

/** One result of "check names" (server: POST …/recipients/resolve). */
export interface ResolveOutcome {
  input: string;
  status: 'resolved' | 'ambiguous' | 'unresolved';
  match: PersonEntry | null;
  candidates: readonly PersonEntry[];
}

const emailSchema = z.email();
export const isValidEmail = (value: string): boolean => emailSchema.safeParse(value).success;

let counter = 0;
const nextId = (): string => `r${Date.now().toString(36)}${(counter++).toString(36)}`;

export interface ParsedRecipient {
  raw: string;
  name?: string | undefined;
  email?: string | undefined;
}

/**
 * Splits typed or pasted text into entries. Separators: `,` `;` and line breaks, except inside
 * quotes or angle brackets ("Test, Bob" <bob@x>). Each entry: `Name <addr>`, `addr` or a name.
 */
export function parseRecipientList(text: string): ParsedRecipient[] {
  const parts: string[] = [];
  let current = '';
  let quoted = false;
  let angle = false;
  for (const ch of text) {
    if (ch === '"' && !angle) quoted = !quoted;
    else if (ch === '<' && !quoted) angle = true;
    else if (ch === '>' && !quoted) angle = false;
    if (!quoted && !angle && (ch === ',' || ch === ';' || ch === '\n' || ch === '\r')) {
      parts.push(current);
      current = '';
    } else {
      current += ch;
    }
  }
  parts.push(current);
  return parts.map((p) => p.trim()).filter(Boolean).map(parseOne);
}

function parseOne(raw: string): ParsedRecipient {
  const angle = /^(.*?)<([^<>]*)>\s*$/.exec(raw);
  if (angle) {
    const name = (angle[1] ?? '').trim().replace(/^"(.*)"$/, '$1').trim();
    return { raw, name: name || undefined, email: (angle[2] ?? '').trim().replace(/^mailto:/i, '') };
  }
  const bare = raw.replace(/^mailto:/i, '').replace(/^"(.*)"$/, '$1').trim();
  return bare.includes('@') ? { raw, email: bare } : { raw, name: bare };
}

/** A chip for a parsed entry: valid address = resolved, malformed address = invalid, name = pending. */
export function recipientFromParsed(p: ParsedRecipient): Recipient {
  if (p.email !== undefined) {
    const ok = isValidEmail(p.email);
    return { id: nextId(), email: p.email, name: p.name, kind: 'person', status: ok ? 'resolved' : 'invalid', source: ok ? 'external' : undefined, input: p.raw };
  }
  return { id: nextId(), email: '', name: p.name ?? p.raw, kind: 'person', status: 'pending', input: p.raw };
}

export function recipientFromEntry(entry: PersonEntry): Recipient {
  return { id: nextId(), email: entry.email, name: entry.name ?? undefined, key: entry.key, source: entry.source, kind: entry.kind, status: 'resolved' };
}

export function recipientsFromInit(list: readonly RecipientInit[] | undefined): Recipient[] {
  return (list ?? []).map((item) =>
    typeof item === 'string'
      ? recipientFromParsed({ raw: item, email: item })
      : recipientFromParsed({ raw: item.email, email: item.email, name: item.name ?? undefined }),
  );
}

/** Apply a check-names result to a pending/unresolved chip. */
export function applyResolution(r: Recipient, outcome: ResolveOutcome): Recipient {
  if (outcome.status === 'resolved' && outcome.match) return { ...recipientFromEntry(outcome.match), id: r.id };
  if (outcome.status === 'ambiguous') return { ...r, status: 'ambiguous', candidates: outcome.candidates };
  return { ...r, status: 'unresolved', candidates: undefined };
}

/** Text that brings a chip back into the input for editing. */
export function recipientText(r: Recipient): string {
  if (r.status !== 'resolved' && r.status !== 'invalid') return r.input ?? r.name ?? r.email;
  if (!r.name || r.name === r.email) return r.email;
  return /[",;<>]/.test(r.name) ? `"${r.name.replace(/"/g, '')}" <${r.email}>` : `${r.name} <${r.email}>`;
}

export const recipientLabel = (r: Pick<Recipient, 'name' | 'email'>): string => r.name || r.email;

/** Chips that block sending (DESIGN.md T11: marked in the field, check names first). */
export const needsAttention = (r: Recipient): boolean => r.status !== 'resolved';

/** Number of avatar tones (chart tokens --chart-1…5). */
export const AVATAR_TONES = 5;

/** Deterministic tone 0…4 for an address (FNV-1a), so a person keeps their colour everywhere. */
export function avatarTone(seed: string): number {
  let hash = 0x811c9dc5;
  for (const ch of seed.toLowerCase()) {
    hash ^= ch.codePointAt(0) ?? 0;
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash % AVATAR_TONES;
}
